import React, { useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Formik, Form, FieldArray, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Plus,
  Trash2,
  ChevronRight,
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Undo,
  Redo,
} from "lucide-react";
import { Switch } from "../../../components/ui/switch";
import { Label } from "../../../components/ui/label";
import { GlobalContext } from "../../../App";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../../components/ui/breadcrumb";
import { insert_customer } from "../../../api/CustomerApi";
import { EditorProvider, useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
const extensions = [StarterKit];

const TiptapMenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2 p-2 border-b border-gray-200 bg-gray-50 rounded-t-md">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={editor.isActive("bold") ? "bg-gray-200" : ""}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={editor.isActive("italic") ? "bg-gray-200" : ""}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 1 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 2 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 3 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="h-4 w-4" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        <Redo className="h-4 w-4" />
      </Button>
    </div>
  );
};

const Tiptap = ({ value, onChange }) => {
  const editor = useEditor({
    extensions,
    content: value || "<p></p>",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  return (
    <div className="border rounded-md">
      <TiptapMenuBar editor={editor} />
      <EditorContent
        editor={editor}
        className="prose max-w-none p-4 min-h-[200px] outline-none w-full"
      />
    </div>
  );
};

// Removed old granular attributeTypes; replaced with simple modes: text, input, list

const countryOptions = [
  { value: "usa", label: "United States" },
  { value: "canada", label: "Canada" },
];

const validationSchema = Yup.object().shape({
  customer_name: Yup.string().required("Customer name is required"),
  // username: Yup.string()
  //   .required("Username is required")
  //   .min(3, "Username must be at least 3 characters")
  //   .max(30, "Username must be at most 30 characters"),
  // password: Yup.string()
  //   .required("Password is required")
  //   .min(8, "Password must be at least 8 characters")
  //   .matches(
  //     /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).+$/,
  //     "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
  //   ),
  customer_address: Yup.string().nullable(),
  customer_city: Yup.string().nullable(),
  customer_state: Yup.string().nullable(),
  customer_zip: Yup.string().nullable(),
  customer_country: Yup.string().required("Customer country is required"),
  customer_number: Yup.string().nullable(),
  flow_backup_number: Yup.string()
    .matches(
      /^\+[1-9]\d{1,14}$/,
      "Phone number must be in valid E.164 format starting with '+'"
    )
    .nullable(),
  dedicated_phone_number: Yup.string()
    .matches(
      /^\+[1-9]\d{1,14}$/,
      "Phone number must be in valid E.164 format starting with '+'"
    )
    .nullable(),

  default_voice_rate: Yup.number()
    .typeError("Default voice rate must be a number")
    .min(0, "Default voice rate cannot be negative")
    .nullable(),
  default_video_rate: Yup.number()
    .typeError("Default video rate must be a number")
    .min(0, "Default video rate cannot be negative")
    .nullable(),

  rates: Yup.array()
    .of(
      Yup.object()
        .shape({
          language: Yup.string().nullable(),
          language_code: Yup.string().nullable(),
          voice_rate: Yup.number()
            .typeError("Voice rate must be a number")
            .min(0, "Voice rate cannot be negative")
            .nullable(),
          video_rate: Yup.number()
            .typeError("Video rate must be a number")
            .min(0, "Video rate cannot be negative")
            .nullable(),
        })
        .test(
          "complete-rate-row",
          "If you set a language, you must provide both voice and video rates; if you set any rate, language is required",
          function (value) {
            const { language_code, voice_rate, video_rate } = value || {};
            const hasAny =
              Boolean(language_code) || voice_rate > 0 || video_rate > 0;
            if (!hasAny) return true;
            const hasLanguage = Boolean(language_code);
            const hasRates =
              voice_rate !== undefined &&
              voice_rate >= 0 &&
              video_rate !== undefined &&
              video_rate >= 0;
            return hasLanguage && hasRates;
          }
        )
    )
    .nullable(),

  access_code: Yup.string().required("Access code is required"),
  script: Yup.string().nullable(),
  // category_code_name: Yup.string().nullable(),
  // category_code_values: Yup.array().of(Yup.string()).nullable(),
});

const AddCustomerForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [attributeOptions, setAttributeOptions] = useState({});
  const [popoverOpen, setPopoverOpen] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const { setToastOpen, setToastMessage, setToastSeverity, languages } =
    useContext(GlobalContext);

  const initialValues = {
    customer_name: "",
    username: "",
    password: "",
    customer_address: "",
    customer_city: "",
    customer_state: "",
    customer_zip: "",
    customer_country: "usa",
    customer_number: "",
    flow_backup_number: "",
    dedicated_phone_number: "",
    default_voice_rate: 0,
    default_video_rate: 0,
    rates: [
      {
        language: "",
        language_code: "",
        voice_rate: 0,
        video_rate: 0,
      },
    ],
    access_code: "",
    notes: "",
    script: "",
    category_code_name: "",
    category_code_values: [],
    attributes: [],
  };

  const createCustomer = async (customerData) => {
    return insert_customer(customerData);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);

      // Prepare the customer data matching the backend structure
      const customerData = {
        // email: values.email,
        username: values.username,
        customer_name: values.customer_name,
        customer_address: values.customer_address,
        customer_city: values.customer_city,
        customer_state: values.customer_state,
        customer_zip: values.customer_zip,
        customer_country: values.customer_country,
        customer_number: values.customer_number,
        flow_backup_number: values.flow_backup_number || null,
        dedicated_phone_number: values.dedicated_phone_number || null,
        default_voice_rate: parseFloat(values.default_voice_rate) || 0,
        default_video_rate: parseFloat(values.default_video_rate) || 0,
        rates: values.rates.map((rate) => ({
          language: rate.language,
          language_code: rate.language_code,
          voice_rate: parseFloat(rate.voice_rate) || 0,
          video_rate: parseFloat(rate.video_rate) || 0,
        })),
        access_code: values.access_code,
        script: values.script || "",
        is_customer_deleted: false,
        password: values.password,
        notes: values.notes,
        category_code_name: values.category_code_name,
        category_code_values: values.category_code_values,
        attributes: values.attributes.map((attr) => {
          const mode = attr.attribute_mode || "text";
          const isList = mode === "list";
          const isInput = mode === "input" || mode === "list";
          const optionsArray = Array.isArray(attr.attribute_options)
            ? attr.attribute_options.filter(
                (v) => typeof v === "string" && v.trim() !== ""
              )
            : [];
          return {
            attribute_name: attr.attribute_name,
            // attribute_value: attr.attribute_value,
            attribute_description: attr.attribute_description || "",
            attribute_subtitle: attr.attribute_subtitle || "",
            attribute_type: isList ? "select" : "text",
            is_input: isInput,
            is_required: attr.is_required || false,
            attribute_options: isList ? optionsArray : [],
          };
        }),
      };

      await createCustomer(customerData);

      setToastOpen(true);
      setToastSeverity("success");
      setToastMessage("Customer added successfully!");
      navigate("/admin/customers");
    } catch (error) {
      console.error("Error saving customer:", error);
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        error.response?.data?.message ||
          "Failed to save customer. Please try again."
      );
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleAttributeTypeChange = (value, index, setFieldValue, values) => {
    setFieldValue(`attributes[${index}].attribute_type`, value);
    if (!["select", "multiselect"].includes(value)) {
      setFieldValue(`attributes[${index}].attribute_options`, "");
      setAttributeOptions((prev) => {
        const updated = { ...prev };
        delete updated[values.attributes[index].attribute_name];
        return updated;
      });
    }
  };

  const handleAttributeOptionsChange = (
    value,
    index,
    setFieldValue,
    values
  ) => {
    setFieldValue(`attributes[${index}].attribute_options`, value);
    if (values.attributes[index].attribute_name) {
      setAttributeOptions((prev) => ({
        ...prev,
        [values.attributes[index].attribute_name]: value
          .split(",")
          .map((opt) => opt.trim()),
      }));
    }
  };

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-center justify-between">
        <Breadcrumb className="mb-0">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => navigate("/admin")}>
                Admin
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => navigate("/admin/customers")}>
                Customers
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>Add New Customer</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <Formik
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form id="customerForm">
            <div className="space-y-2">
              {/* Customer Information */}
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle>Customer Information</CardTitle>
                  <CardDescription>Basic customer details</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="customer_name">Customer Name *</Label>
                      <Field
                        as={Input}
                        id="customer_name"
                        name="customer_name"
                        placeholder="Enter customer name"
                      />
                      <ErrorMessage
                        name="customer_name"
                        component="div"
                        className="text-xs text-red-500 font-light"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="access_code">Access Code *</Label>
                      <Field
                        as={Input}
                        id="access_code"
                        name="access_code"
                        placeholder="Enter access code"
                      />
                      <ErrorMessage
                        name="access_code"
                        component="div"
                        className="text-xs text-red-500 font-light"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="username">Username *</Label>
                      <Field
                        as={Input}
                        id="username"
                        name="username"
                        placeholder="Enter username"
                      />
                      <ErrorMessage
                        name="username"
                        component="div"
                        className="text-xs text-red-500 font-light"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="password">Password *</Label>
                      <Field
                        as={Input}
                        id="password"
                        name="password"
                        type="password"
                        placeholder="Enter password"
                      />
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="text-xs text-red-500 font-light"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                  <CardDescription>
                    Phone numbers and communication details
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="customer_number">Phone Number</Label>
                      <Field
                        as={Input}
                        id="customer_number"
                        name="customer_number"
                        placeholder="Enter phone number"
                      />
                      <ErrorMessage
                        name="customer_number"
                        component="div"
                        className="text-xs text-red-500 font-light"
                      />
                    </div>
                    <div></div>
                    <div className="space-y-1">
                      <Label htmlFor="dedicated_phone_number">
                        Dedicated Phone Number
                      </Label>
                      <Field
                        as={Input}
                        id="dedicated_phone_number"
                        name="dedicated_phone_number"
                        placeholder="Enter dedicated phone number (E.164)"
                      />
                      <ErrorMessage
                        name="dedicated_phone_number"
                        component="div"
                        className="text-xs text-red-500 font-light"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="flow_backup_number">Backup Number</Label>
                      <Field
                        as={Input}
                        id="flow_backup_number"
                        name="flow_backup_number"
                        placeholder="Enter backup phone number"
                      />
                      <ErrorMessage
                        name="flow_backup_number"
                        component="div"
                        className="text-xs text-red-500 font-light"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Address Information */}
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle>Address Information</CardTitle>
                  <CardDescription>Customer location details</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="space-y-1">
                      <Label htmlFor="customer_country">Country</Label>
                      <Field name="customer_country">
                        {({ field, form }) => (
                          <Select
                            value={field.value}
                            onValueChange={(value) =>
                              form.setFieldValue(field.name, value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent>
                              {countryOptions.map((country) => (
                                <SelectItem
                                  key={country.value}
                                  value={country.value}
                                >
                                  {country.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </Field>
                      <ErrorMessage
                        name="customer_country"
                        component="div"
                        className="text-xs text-red-500 font-light"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="customer_address">Address</Label>
                      <Field
                        as={Input}
                        id="customer_address"
                        name="customer_address"
                        placeholder="Enter address"
                      />
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="space-y-1">
                        <Label htmlFor="customer_city">City</Label>
                        <Field
                          as={Input}
                          id="customer_city"
                          name="customer_city"
                          placeholder="Enter city"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="customer_state">State/Province</Label>
                        <Field
                          as={Input}
                          id="customer_state"
                          name="customer_state"
                          placeholder="Enter state/province"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="customer_zip">ZIP/Postal Code</Label>
                        <Field
                          as={Input}
                          id="customer_zip"
                          name="customer_zip"
                          placeholder="Enter ZIP/Postal code"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Service Settings */}
              <Card className="border border-gray-200">
                <CardHeader className="flex-1">
                  <CardTitle>Rates</CardTitle>
                  <CardDescription>Customer rates</CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="flex gap-4 pr-4">
                    <div className="space-y-1">
                      <Label htmlFor="default_voice_rate" className="text-xs">
                        Default Voice Rate ($/min)
                      </Label>
                      <div className="w-40">
                        <Field
                          as={Input}
                          id="default_voice_rate"
                          name="default_voice_rate"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="default_video_rate" className="text-xs">
                        Default Video Rate ($/min)
                      </Label>
                      <div className="w-40">
                        <Field
                          as={Input}
                          id="default_video_rate"
                          name="default_video_rate"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-3 mt-6">
                    <FieldArray name="rates">
                      {({ push, remove, form }) => (
                        <div className="space-y-4 col-span-3">
                          <div className="space-y-2">
                            {values.rates && values.rates.length > 0 ? (
                              <div className="space-y-6">
                                {values.rates.map((rate, index) => (
                                  <div
                                    key={index}
                                    className="grid grid-cols-12 gap-4 items-end"
                                  >
                                    <div className="col-span-4">
                                      <Label className={"mb-2"}>Language</Label>
                                      <Popover
                                        open={popoverOpen[`rate-${index}`]}
                                        onOpenChange={(open) => {
                                          setPopoverOpen((prev) => ({
                                            ...prev,
                                            [`rate-${index}`]: open,
                                          }));
                                        }}
                                      >
                                        <PopoverTrigger asChild>
                                          <Button
                                            variant="outline"
                                            role="combobox"
                                            className={cn(
                                              "w-full justify-between",
                                              !rate.language &&
                                                "text-muted-foreground"
                                            )}
                                          >
                                            {rate.language
                                              ? languages.find(
                                                  (lang) =>
                                                    lang.code ===
                                                    rate.language_code
                                                )?.name || "Select language..."
                                              : "Select language..."}
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[300px] p-0">
                                          <Command>
                                            <CommandInput
                                              placeholder="Search language..."
                                              value={
                                                searchTerms[`rate-${index}`] ||
                                                ""
                                              }
                                              onValueChange={(value) => {
                                                setSearchTerms((prev) => ({
                                                  ...prev,
                                                  [`rate-${index}`]: value,
                                                }));
                                              }}
                                            />
                                            <CommandList>
                                              <CommandEmpty>
                                                No language found.
                                              </CommandEmpty>
                                              <CommandGroup>
                                                {languages
                                                  .filter(
                                                    (lang) =>
                                                      lang.name
                                                        .toLowerCase()
                                                        .includes(
                                                          (
                                                            searchTerms[
                                                              `rate-${index}`
                                                            ] || ""
                                                          ).toLowerCase()
                                                        ) ||
                                                      lang.code
                                                        .toLowerCase()
                                                        .includes(
                                                          (
                                                            searchTerms[
                                                              `rate-${index}`
                                                            ] || ""
                                                          ).toLowerCase()
                                                        )
                                                  )
                                                  .map((lang) => (
                                                    <CommandItem
                                                      key={lang.code}
                                                      value={lang.code}
                                                      onSelect={() => {
                                                        // Set both language name and code
                                                        form.setFieldValue(
                                                          `rates[${index}].language`,
                                                          lang.name
                                                        );
                                                        form.setFieldValue(
                                                          `rates[${index}].language_code`,
                                                          lang.code
                                                        );
                                                        setPopoverOpen(
                                                          (prev) => ({
                                                            ...prev,
                                                            [`rate-${index}`]: false,
                                                          })
                                                        );
                                                        setSearchTerms(
                                                          (prev) => ({
                                                            ...prev,
                                                            [`rate-${index}`]:
                                                              "",
                                                          })
                                                        );
                                                      }}
                                                    >
                                                      <Check
                                                        className={cn(
                                                          "mr-2 h-4 w-4",
                                                          rate.language_code ===
                                                            lang.code
                                                            ? "opacity-100"
                                                            : "opacity-0"
                                                        )}
                                                      />
                                                      {lang.name} ({lang.code})
                                                    </CommandItem>
                                                  ))}
                                              </CommandGroup>
                                            </CommandList>
                                          </Command>
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                    <div className="col-span-3">
                                      <Label className={"mb-2"}>
                                        Voice Rate ($/min)
                                      </Label>
                                      <Field
                                        as={Input}
                                        name={`rates[${index}].voice_rate`}
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <div className="col-span-3">
                                      <Label className={"mb-2"}>
                                        Video Rate ($/min)
                                      </Label>
                                      <Field
                                        as={Input}
                                        name={`rates[${index}].video_rate`}
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <div className="col-span-2 flex items-end">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => remove(index)}
                                        className="text-red-500 hover:text-red-700"
                                        disabled={values.rates.length === 1}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center text-sm py-2 text-gray-500">
                                <p>No language rates added yet.</p>
                              </div>
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                push({
                                  language: "",
                                  voice_rate: 0,
                                  video_rate: 0,
                                })
                              }
                              className="mt-2"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Language Rate
                            </Button>
                          </div>
                        </div>
                      )}
                    </FieldArray>
                  </div>
                </CardContent>
              </Card>

              {/* Category Codes */}
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle>Category Codes</CardTitle>
                  <CardDescription>
                    Assign category code name and values
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="category_code_name">
                        Category Code Name
                      </Label>
                      <Field
                        as={Input}
                        id="category_code_name"
                        name="category_code_name"
                        placeholder="Enter category code name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Category Code Values</Label>
                      <FieldArray name="category_code_values">
                        {({ remove, push }) => (
                          <div className="flex flex-col">
                            {values.category_code_values &&
                            values.category_code_values.length > 0 ? (
                              <div className="space-y-2">
                                {values.category_code_values.map(
                                  (value, index) => (
                                    <div
                                      key={index}
                                      className="flex items-center gap-2"
                                    >
                                      <Field
                                        as={Input}
                                        name={`category_code_values.${index}`}
                                        placeholder={`Value ${index + 1}`}
                                        className="flex-1"
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => remove(index)}
                                      >
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                      </Button>
                                    </div>
                                  )
                                )}
                              </div>
                            ) : (
                              <div className="text-center text-sm py-2 text-gray-500">
                                <p>No category code values added yet.</p>
                              </div>
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => push("")}
                              className="mt-2"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Value
                            </Button>
                          </div>
                        )}
                      </FieldArray>
                      <p className="text-xs text-gray-500 mt-4">
                        Add multiple values that will be associated with this
                        category code.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle>Notes</CardTitle>
                  <CardDescription>Customer-specific notes</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <Field name="notes">
                      {({ field, form }) => (
                        <Tiptap
                          value={field.value}
                          onChange={(newValue) =>
                            form.setFieldValue("notes", newValue)
                          }
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="notes"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>
                </CardContent>
              </Card>
              {/* Script */}
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle>Script</CardTitle>
                  <CardDescription>
                    Customer-specific script for interpreters
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <Field name="script">
                      {({ field, form }) => (
                        <Tiptap
                          value={field.value}
                          onChange={(newValue) =>
                            form.setFieldValue("script", newValue)
                          }
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="script"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Custom Attributes */}
              <FieldArray name="attributes">
                {({ remove, push }) => (
                  <Card className="border border-gray-200">
                    <CardHeader>
                      <div>
                        <CardTitle>Custom Attributes</CardTitle>
                        <CardDescription>
                          Add custom fields to store additional information
                        </CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {values.attributes && values.attributes.length > 0 ? (
                        values.attributes.map((attr, idx) => (
                          <div
                            key={idx}
                            className="p-4 border border-gray-200 rounded-lg"
                          >
                            <div className="flex justify-between items-center">
                              <h4 className="font-medium">
                                Attribute {idx + 1}
                              </h4>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => remove(idx)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div className="space-y-1">
                                <Label htmlFor={`attribute_name_${idx}`}>
                                  Attribute Name *
                                </Label>
                                <Field
                                  as={Input}
                                  id={`attribute_name_${idx}`}
                                  name={`attributes[${idx}].attribute_name`}
                                  placeholder="e.g., first_name"
                                />
                                <ErrorMessage
                                  name={`attributes[${idx}].attribute_name`}
                                  component="div"
                                  className="text-xs text-red-500 font-light"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor={`attribute_label_${idx}`}>
                                  Attribute Subtitle *
                                </Label>
                                <Field
                                  as={Input}
                                  id={`attribute_label_${idx}`}
                                  name={`attributes[${idx}].attribute_label`}
                                  placeholder="e.g., Insert full name"
                                />
                                <ErrorMessage
                                  name={`attributes[${idx}].attribute_label`}
                                  component="div"
                                  className="text-xs text-red-500 font-light"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor={`attribute_mode_${idx}`}>
                                  Field Mode
                                </Label>
                                <Field
                                  name={`attributes[${idx}].attribute_mode`}
                                >
                                  {({ field, form }) => (
                                    <Select
                                      value={field.value || "text"}
                                      onValueChange={(value) => {
                                        form.setFieldValue(
                                          `attributes[${idx}].attribute_mode`,
                                          value
                                        );
                                        if (value !== "list") {
                                          form.setFieldValue(
                                            `attributes[${idx}].attribute_options`,
                                            []
                                          );
                                        }
                                      }}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select mode" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="text">
                                          Text
                                        </SelectItem>
                                        <SelectItem value="input">
                                          Input
                                        </SelectItem>
                                        <SelectItem value="list">
                                          List
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                  )}
                                </Field>
                              </div>

                              {attr.attribute_mode === "list" && (
                                <div className="space-y-1">
                                  <Label>List Values</Label>
                                  <FieldArray
                                    name={`attributes[${idx}].attribute_options`}
                                  >
                                    {({ remove, push }) => (
                                      <div className="flex flex-col">
                                        {Array.isArray(
                                          attr.attribute_options
                                        ) &&
                                        attr.attribute_options.length > 0 ? (
                                          <div className="space-y-2">
                                            {attr.attribute_options.map(
                                              (value, optIdx) => (
                                                <div
                                                  key={optIdx}
                                                  className="flex items-center gap-2"
                                                >
                                                  <Field
                                                    as={Input}
                                                    name={`attributes[${idx}].attribute_options.${optIdx}`}
                                                    placeholder={`Value ${
                                                      optIdx + 1
                                                    }`}
                                                    className="flex-1"
                                                  />
                                                  <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() =>
                                                      remove(optIdx)
                                                    }
                                                  >
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                  </Button>
                                                </div>
                                              )
                                            )}
                                          </div>
                                        ) : (
                                          <div className="text-center text-sm py-2 text-gray-500">
                                            <p>No values added yet.</p>
                                          </div>
                                        )}
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() => push("")}
                                          className="mt-2"
                                        >
                                          <Plus className="h-4 w-4 mr-2" />
                                          Add Value
                                        </Button>
                                      </div>
                                    )}
                                  </FieldArray>
                                </div>
                              )}
                              <div className="space-y-1">
                                <div className="flex items-center space-x-2">
                                  <Switch
                                    id={`required-${idx}`}
                                    checked={attr.is_required}
                                    onCheckedChange={(checked) =>
                                      setFieldValue(
                                        `attributes[${idx}].is_required`,
                                        checked
                                      )
                                    }
                                  />
                                  <Label
                                    htmlFor={`required-${idx}`}
                                    className="!mt-0"
                                  >
                                    Required
                                  </Label>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-2 text-gray-500">
                          <p>No custom attributes added yet.</p>
                        </div>
                      )}
                      <div className="flex justify-center mt-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            push({
                              attribute_name: "",
                              attribute_label: "",
                              attribute_type: "text",
                              attribute_options: "",
                              is_required: false,
                              is_input: false,
                              value: "",
                            })
                          }
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          {values.attributes && values.attributes.length > 0
                            ? "Add Another Attribute"
                            : "Add Attribute"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </FieldArray>

              {/* Form Actions */}
              <div className="fixed bottom-0 left-0 right-0 w-full bg-white p-4 flex justify-end space-x-4 mt-6 border-t border-gray-200 shadow-lg z-[1000]">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/admin/customers")}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : "Add Customer"}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddCustomerForm;
